/**
 * Sales-per-hour sparkline with vertical markers for price changes.
 * Pure SVG so no chart lib dependency.
 */
import { useMemo } from 'react';

const SalesVelocityChart = ({ points = [], markers = [], windowHours = 48 }) => {
  const data = useMemo(() => {
    if (!points.length) return null;
    const maxSales = Math.max(1, ...points.map(p => p.tickets_sold));
    const width = 800;
    const height = 120;
    const padX = 8;
    const padY = 12;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;
    
    // Map points to coords; oldest left, newest right.
    const stepX = points.length > 1 ? innerW / (points.length - 1) : innerW;
    const coords = points.map((p, i) => ({
      x: padX + i * stepX,
      y: padY + innerH - (p.tickets_sold / maxSales) * innerH,
      raw: p
    }));
    
    // Smooth line via cubic bezier between points (Catmull-Rom -> bezier approx).
    const path = coords.map((c, i) => i === 0 ? `M ${c.x.toFixed(1)} ${c.y.toFixed(1)}` : `L ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(' ');
    const areaPath = `${path} L ${coords[coords.length-1].x.toFixed(1)} ${padY+innerH} L ${coords[0].x.toFixed(1)} ${padY+innerH} Z`;
    
    // Map markers to x position based on hours_ago (windowHours = right edge of newest, 0 = newest)
    // x for hours_ago = padX + (windowHours - hours_ago) / windowHours * innerW
    const markerCoords = markers.map(m => {
      const ratio = Math.min(1, Math.max(0, (windowHours - m.hours_ago) / windowHours));
      return {
        x: padX + ratio * innerW,
        ...m
      };
    });
    
    return { width, height, padX, padY, innerW, innerH, coords, path, areaPath, markerCoords, maxSales };
  }, [points, markers, windowHours]);
  
  if (!data) {
    return (
      <div className="px-4 py-8 text-center text-zinc-500 text-sm">
        Sin datos de ventas para graficar todavía.
      </div>
    );
  }
  
  const { width, height, padX, padY, innerW, innerH, path, areaPath, markerCoords, maxSales } = data;
  
  return (
    <div className="px-4 py-4" data-testid="sales-velocity-chart">
      <div className="flex items-center justify-between mb-2 text-[10px] text-zinc-500">
        <span>Hace {windowHours}h</span>
        <span>Pico: {maxSales} t/h</span>
        <span>Ahora</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="none" style={{ height: 140 }}>
        <defs>
          <linearGradient id="sales-line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#22c55e" stopOpacity="1"/>
          </linearGradient>
          <linearGradient id="sales-area-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.25"/>
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0"/>
          </linearGradient>
        </defs>
        
        {/* Horizontal grid lines */}
        {[0.25, 0.5, 0.75].map((p, i) => (
          <line key={i} x1={padX} y1={padY + innerH * p} x2={padX + innerW} y2={padY + innerH * p} stroke="rgba(255,255,255,0.05)" strokeDasharray="2 4"/>
        ))}
        
        {/* Price change markers (vertical lines drawn BEHIND line) */}
        {markerCoords.map((m, i) => {
          const isUrgent = m.source === 'suggestion_urgent';
          const isHot = m.source === 'suggestion_hot';
          const color = isUrgent ? '#ef4444' : isHot ? '#f97316' : '#a78bfa';
          return (
            <g key={i}>
              <line x1={m.x} y1={padY} x2={m.x} y2={padY + innerH} stroke={color} strokeWidth="1.5" strokeDasharray="3 3" opacity="0.7"/>
              <circle cx={m.x} cy={padY} r="4" fill={color}/>
            </g>
          );
        })}
        
        {/* Area fill */}
        <path d={areaPath} fill="url(#sales-area-grad)"/>
        
        {/* Line */}
        <path d={path} fill="none" stroke="url(#sales-line-grad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        
        {/* Dots on each data point with sales > 0 */}
        {data.coords.map((c, i) => c.raw.tickets_sold > 0 && (
          <circle key={i} cx={c.x} cy={c.y} r="2.5" fill="#22c55e">
            <title>{`${c.raw.tickets_sold} tickets · ${c.raw.hours_ago === 0 ? 'última hora' : `hace ${c.raw.hours_ago}h`}`}</title>
          </circle>
        ))}
      </svg>
      
      {/* Marker legend */}
      {markerCoords.length > 0 && (
        <div className="mt-3 flex items-center gap-3 flex-wrap text-[10px]">
          <span className="text-zinc-500">Marcadores:</span>
          <span className="flex items-center gap-1 text-orange-400">
            <span className="w-2 h-2 rounded-full bg-orange-500" /> Sugerencia Hot
          </span>
          <span className="flex items-center gap-1 text-red-400">
            <span className="w-2 h-2 rounded-full bg-red-500" /> Sugerencia Urgent
          </span>
          <span className="flex items-center gap-1 text-violet-400">
            <span className="w-2 h-2 rounded-full bg-violet-500" /> Cambio manual
          </span>
          <span className="ml-auto text-zinc-500">{markerCoords.length} cambio{markerCoords.length !== 1 ? 's' : ''} de precio</span>
        </div>
      )}
    </div>
  );
};

export default SalesVelocityChart;
